// AI Tools Status Widget — Home Dashboard tile that surfaces SageMaster
// connection state, weekly Helix reminder, and the active session countdown.
// Visible only to traders who have unlocked Tier 3 (i.e., tier2Graduate()).
//
// Tier 3 is part of the SageMaster ecosystem (https://sagemaster.com).
// Connection status is mocked via localStorage flag "flexx_sagemaster_connected"
// — a real SageMaster API integration would replace this in the future.

import React, { useEffect, useState } from "react";
import { THEME } from "./state";
import { TIER3_EMERALD } from "./LessonsHub.Tier3";
import { getSessionState, formatDuration, getUserTz } from "./flexxTime";

const SAGEMASTER_URL = "https://sagemaster.com";
const CONN_KEY = "flexx_sagemaster_connected";
const HELIX_DAY = 0; // Sunday — Helix Sunday Report

function readConnected() {
  try { return localStorage.getItem(CONN_KEY) === "1"; } catch (_) { return false; }
}

function nextHelixSunday(now) {
  const d = new Date(now);
  const day = d.getDay();
  const daysUntil = (HELIX_DAY - day + 7) % 7 || 7;
  const next = new Date(d);
  next.setDate(d.getDate() + daysUntil);
  next.setHours(9, 0, 0, 0);
  return next;
}

export default function AiToolsStatus() {
  const tz = getUserTz();
  const [connected, setConnected] = useState(readConnected);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const sessionState = getSessionState(now, tz);
  const primary = sessionState.primary;
  const isOff = !primary;
  const upcoming = sessionState.upcoming;

  let countdownLabel, countdownMs, countdownTitle;
  if (primary && primary.msToEnd != null) {
    countdownTitle = `${primary.short || primary.name} ENDS IN`;
    countdownMs = Math.max(0, primary.msToEnd);
    countdownLabel = formatDuration(countdownMs);
  } else if (upcoming && upcoming.msToStart != null) {
    countdownTitle = `${upcoming.short || upcoming.name} OPENS IN`;
    countdownMs = Math.max(0, upcoming.msToStart);
    countdownLabel = formatDuration(countdownMs);
  } else {
    countdownTitle = "SESSION";
    countdownLabel = "—";
  }

  const helixNext = nextHelixSunday(now);
  const helixMs = Math.max(0, helixNext.getTime() - now.getTime());
  const helixHrs = Math.round(helixMs / 3_600_000);
  const helixDays = Math.floor(helixHrs / 24);
  const helixLabel = helixDays >= 1
    ? `Helix Sunday Report · in ${helixDays}d ${helixHrs % 24}h`
    : `Helix Sunday Report · in ${helixHrs}h`;

  const toggleConnected = () => {
    const next = !connected;
    try { localStorage.setItem(CONN_KEY, next ? "1" : "0"); } catch (_) {}
    setConnected(next);
  };

  return (
    <div data-testid="ai-tools-status-widget" className="flexx-card" style={{
      borderLeft: `3px solid ${TIER3_EMERALD}`,
      boxShadow: `0 0 24px rgba(16,185,129,0.10)`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: "monospace", color: TIER3_EMERALD, fontSize: 10, letterSpacing: 2 }}>TIER 3 · AI TOOLS STATUS</div>
          <div style={{ color: THEME.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>SageMaster Cockpit</div>
        </div>
        <a data-testid="ai-tools-open-sagemaster" href={SAGEMASTER_URL} target="_blank" rel="noopener noreferrer"
          style={{
            padding: "6px 12px", borderRadius: 6,
            background: `${TIER3_EMERALD}18`, border: `1px solid ${TIER3_EMERALD}`,
            color: TIER3_EMERALD, fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5,
            textDecoration: "none",
          }}>
          OPEN SAGEMASTER ↗
        </a>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        {/* Connection */}
        <div data-testid="ai-tools-connection-row" style={statBox(connected ? TIER3_EMERALD : THEME.border)}>
          <div style={statLabel}>CONNECTION</div>
          <div style={{
            ...statValue, color: connected ? TIER3_EMERALD : "#FF6B35", display: "flex", alignItems: "center", gap: 8,
          }}>
            <span data-testid="ai-tools-connection-dot" style={{
              width: 9, height: 9, borderRadius: 9,
              background: connected ? TIER3_EMERALD : "#FF6B35",
              boxShadow: connected ? `0 0 10px ${TIER3_EMERALD}` : "none",
            }} />
            {connected ? "Connected" : "Not Linked"}
          </div>
          <button data-testid="ai-tools-connection-toggle" onClick={toggleConnected}
            style={{
              marginTop: 8, padding: "5px 10px", borderRadius: 5,
              background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.mute,
              cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
            }}>
            {connected ? "Disconnect" : "Mark as Connected"}
          </button>
        </div>

        {/* Session countdown */}
        <div data-testid="ai-tools-session-row" style={statBox(TIER3_EMERALD)}>
          <div style={statLabel}>{countdownTitle}</div>
          <div data-testid="ai-tools-session-countdown" style={{ ...statValue, color: isOff ? THEME.mute : TIER3_EMERALD, fontFamily: "monospace" }}>
            {countdownLabel}
          </div>
          <div style={{ color: THEME.mute, fontSize: 10, fontFamily: "monospace", letterSpacing: 1, marginTop: 6 }}>
            {primary ? `Now: ${primary.name}` : `Off-session · ${tz}`}
          </div>
        </div>

        {/* Helix reminder */}
        <div data-testid="ai-tools-helix-row" style={statBox(TIER3_EMERALD)}>
          <div style={statLabel}>WEEKLY REMINDER</div>
          <div data-testid="ai-tools-helix-reminder" style={{ ...statValue, color: THEME.text, fontSize: 14, lineHeight: 1.4 }}>
            {helixLabel}
          </div>
          <div style={{ color: THEME.mute, fontSize: 10, fontFamily: "monospace", letterSpacing: 1, marginTop: 6 }}>
            Open DeepSage every Sunday 9:00
          </div>
        </div>
      </div>

      {!connected && (
        <div data-testid="ai-tools-not-connected-banner" style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: `${TIER3_EMERALD}10`, border: `1px dashed ${TIER3_EMERALD}55`,
          color: THEME.text, fontSize: 12, lineHeight: 1.6,
        }}>
          <b style={{ color: TIER3_EMERALD }}>Not subscribed to SageMaster?</b>
          {" "}Tier 3 leverages the full SageMaster AI ecosystem.
          {" "}<a href={SAGEMASTER_URL} target="_blank" rel="noopener noreferrer" style={{ color: TIER3_EMERALD, textDecoration: "underline" }}>
            Subscribe at sagemaster.com →
          </a>
        </div>
      )}
    </div>
  );
}

const statBox = (border) => ({
  background: "#0d1218",
  border: `1px solid ${border}55`,
  borderRadius: 10,
  padding: 12,
});
const statLabel = {
  fontSize: 10, letterSpacing: 1.5, color: THEME.mute, fontFamily: "monospace", marginBottom: 6, textTransform: "uppercase",
};
const statValue = {
  fontSize: 18, fontWeight: 800,
};
